<template>
  <div class="py-6 px-8">
    <h-header back="../vendors" />

    <Loading v-if="isLoading" />

    <v-alert v-if="!isLoading && !vendor && !vendor.id" type="warning" outlined>
      {{ $t('vendors.hints.vendorNotFound') }}
    </v-alert>

    <div v-if="!isLoading && vendor.id">
      <div class="d-flex align-center">
        <img
          v-if="vendor.logo"
          :src="vendor.logo.url"
          :alt="vendor.name"
          class="d-block mr-5"
          height="auto"
          width="100"
        />
        <div class="d-block">
          <p class="text-uppercase grey--text caption mt-4 mb-0">
            {{ $t('priceTable.label.vendor') }}
          </p>

          <h2>
            {{ vendor ? vendor.name : '---' }}
          </h2>

          <p class="caption font-weight-bold grey--text">
            {{ vendor ? formatDocument(vendor.document) : '---' }}
          </p>
        </div>
      </div>

      <v-row class="filters--single mb-4">
        <v-col cols="12" sm="12" md="6" lg="3">
          <v-text-field
            v-model="search"
            :label="$t(`filters.search`)"
            append-icon="mdi-magnify"
            hide-details
            single-line
            clearable
            outlined
            small
            dense
          />
        </v-col>

        <v-col cols="12" sm="12" md="6" lg="2">
          <v-autocomplete
            v-model="selectedCategories"
            @change="applyCategoriesFilter()"
            :no-data-text="$t('filters.noData')"
            :label="$t('filters.category')"
            :loading="isCategoryLoading"
            :items="categories"
            item-value="id"
            item-text="name"
            autocomplete="one-time-code"
            clearable
            outlined
            multiple
            small
            dense
          />
        </v-col>
      </v-row>

      <v-card class="pa-3" outlined tile>
        <v-data-table
          :items="filteredPriceTableItems"
          :headers="subHeaders"
          :loading="isPriceTableLoading"
          :no-data-text="$t('table.noResults')"
          :loading-text="$t('table.loading')"
          :items-per-page="10"
          :search="search"
          mobile-breakpoint="20"
          hide-default-footer
          disable-sort
        >
          <template v-slot:[`item.base_price`]="{ item }">
            {{ item.base_price ? formatMoney(item.base_price) : '---' }}
          </template>
          <template v-slot:[`item.combo_price`]="{ item }">
            {{ item.combo_price ? formatMoney(item.combo_price) : '---' }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-btn @click="editItemPrice(item)" small icon>
                <v-icon small> mdi-pencil </v-icon>
              </v-btn>
              <v-spacer class="mx-1" />
              <v-btn @click="deleteItemPrice(item)" icon small>
                <v-icon small> mdi-delete </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <v-divider class="mb-3 grey lighten-2" />

        <div class="text-right">
          <v-btn
            @click="createItemPrice(item)"
            color="primary"
            elevation="0"
            large
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2" small>mdi-plus</v-icon>
              <span class="body-2">
                {{ $t('itemPrice.action.addPrice') }}
              </span>
            </div>
          </v-btn>
        </div>
      </v-card>
    </div>

    <ItemPriceCreate ref="createItemPrice" />
    <ItemPriceDelete />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import {
  priceTableHeaders,
  priceTableSubItemHeaders,
} from '@/views/priceTable/listHeaders'
import {
  catalogMonetary,
  catalogDateWithoutTime,
} from '@/util/catalog'
import { formatDocument } from '@/util/document'

import Loading from '../components/app/Loading'
import ItemPriceDelete from '@/views/itemPrice/components/ItemPriceDelete'
import ItemPriceCreate from '@/views/itemPrice/components/ItemPriceCreate'

export default {
  name: 'PriceTable',

  components: {
    Loading,
    ItemPriceDelete,
    ItemPriceCreate,
  },

  watch: {
    priceTable() {
      this.applyCategoriesFilter()
    },
  },

  computed: {
    ...mapGetters([
      'isLoading',
      'isPriceTableLoading',
      'isCategoryLoading',
      'priceTable',
      'vendor',
      'vendorLogoPath',
      'itemsPerPage',
      'categories',
    ]),
  },

  methods: {
    ...mapActions([
      'fetchVendor',
      'fetchPriceTable',
      'handleDeleteItemPriceModal',
      'handleOpenCreateEditItemPriceModal',
      'fetchCategories',
    ]),

    createItemPrice() {
      this.item = null
      this.itemPriceId = null
      this.basePrice = 0
      this.comboPrice = 0
      this.comboEnabler = false

      this.handleOpenCreateEditItemPriceModal()
      this.$nextTick(() => this.$refs.createItemPrice.resetItemPriceFields())
    },

    editItemPrice(item) {
      this.item = item.item
      this.itemPriceId = item.id
      this.basePrice = item.base_price
      this.comboPrice = item.combo_price
      this.comboEnabler = item.combo_enabler

      this.handleOpenCreateEditItemPriceModal(item.id)
    },

    deleteItemPrice(item) {
      this.handleDeleteItemPriceModal(item.id)
    },

    applyCategoriesFilter() {
      if (this.selectedCategories.length) {
        this.filteredPriceTableItems = this.priceTable.items.filter((item) => {
          return this.selectedCategories.includes(item.item.category.id)
        })
      } else {
        this.filteredPriceTableItems = this.priceTable.items
      }
    },

    // fetchPriceTablePagination() {
    //   this.fetchPriceTable(this.vendor?.price_table?.id)
    // },
  },

  data() {
    return {
      formatDocument,
      headers: priceTableHeaders,
      subHeaders: priceTableSubItemHeaders,
      formatDate: catalogDateWithoutTime,
      formatMoney: catalogMonetary,
      item: null,
      itemPriceId: null,
      basePrice: 0,
      comboPrice: 0,
      comboEnabler: false,
      isAnUpdate: false,
      vendorId: this.$route?.params?.id,
      search: '',
      selectedCategories: [],
      filteredPriceTableItems: [],
    }
  },

  mounted() {
    Promise.resolve(this.fetchVendor(this.vendorId)).then(async () => {
      await this.fetchPriceTable(this.vendor?.price_table?.id)
      this.filteredPriceTableItems = this.priceTable.items
      this.fetchCategories('filter')
    })
  },
}
</script>
